<template>
  <v-autocomplete
    v-model="selected"
    :items="selectorOptions"
    :loading="loading"
    :label="label"
    item-value="id"
    item-text="name"
    :disabled="disabled"
    :search-input.sync="search"
    no-data-text="Ничего не найдено"
    prepend-inner-icon="mdi mdi-reload mdi-18px"
    no-filter
    multiple
    :rules="rule"
    @click:prepend-inner="refresh"
  >
    <!-- Чипсы в инпуте -->
    <template #selection></template>
    <template #append>
      <span v-if="selected.length <= 1" class="mr-2 mt-n1" style="max-width: 130px;">
        <v-chip small v-for="id in selected" :key="`queue_selector${id}`">
          <span class="sliced-name">{{ allSelectorOptions.find(p => p.id === id).name }}</span>
        </v-chip>
      </span>

      <span v-else class="mt-n1">
        <v-chip small style="max-width: 130px;">
          <span class="sliced-name">{{ allSelectorOptions.find(p => p.id === selected[0]).name }}</span>
        </v-chip>
        <span class="grey--text caption">(+{{ selected.length - 1 }})</span>
      </span>
    </template>

    <!-- Убираем чекбоксы в меню -->
    <template #item="data">
      <template>
        <v-list-item-content>
          <v-list-item-title v-html="data.item.name"></v-list-item-title>
        </v-list-item-content>
      </template>
    </template>

    <!-- Строка "Выбрать всё" -->
    <template #prepend-item>
      <v-list-item v-if="!single" ripple @click="toggleAll">
        <v-list-item-action>
          <v-icon>
            {{ icon }}
          </v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>{{ toggleAllText }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider v-if="!single" class="mt-2"></v-divider>
    </template>
  </v-autocomplete>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'

export default {
  name: 'QueueSelector',
  props: {
    value: {
      required: true,
    },
    single: {
      type: Boolean,
      default: false,
    },
    noRules: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'Очереди',
    },
    projectIds: {
      type: Array,
      default() {return []}
    },
    dontLinkToProject: {
      type: Boolean,
      default: false,
    },

    helpersIds: Array,
  },

  data() {
    return {
      allSelectorOptions: [],
      selectorOptions: [],
      loading: false,
      search: null,
      disabled: true,

      rule: [
        v => {
          if (this.noRules) return true
          else
            return !!v.length || 'Данное поле обязательно'
        }
      ],

      debounceRequest: null,
    }
  },

  computed: {
    ...mapGetters({}),

    selected: {
      get() {
        return this.value
      },
      set(selected) {
        this.$emit('input', selected)
      },
    },

    allItems() {
      return this.selected.length === this.selectorOptions.length
    },

    moreItems() {
      return this.selected.length > 0 && !this.allItems
    },

    icon() {
      if (this.allItems) return 'mdi-close-box'
      if (this.moreItems) return 'mdi-plus-box'
      return 'mdi-checkbox-blank-outline'
    },

    toggleAllText() {
      if (this.allItems) return 'Отменить все'
      if (this.moreItems) return 'Выбрать все'
      return 'Выбрать все'
    },

    filters() {
      let filters = ''
      if (Array.isArray(this.projectIds) && this.projectIds.length > 0) {
        filters = `project_ids: [${this.projectIds.join(', ')}]`
      } else if (typeof this.projectIds === 'string' && this.projectIds !== '') {
        filters = `project_ids: [${this.projectIds}]`
      }

      if (this.search && this.search !== '') {
        filters = `${filters}, search: "${this.search}"`
      }

      return filters === '' ? '' : `(${filters})`
    },
  },

  watch: {
    search(val, oldVal) {
      if (val === oldVal || val === null || oldVal === null) return
      if (this.debounceRequest == null) return
      this.debounceRequest()
    },

    selected(val) {
      if (this.single) {
        if (val.length > 1) this.value.shift()
      }
    },

    projectIds: {
      handler(val) {
        if (this.dontLinkToProject === true) return
        
        if (val.length === 0) {
          this.selected = []
          this.selectorOptions = []
          this.allSelectorOptions = []
          this.disabled = true
        } else {
          this.refresh()
        }
      },
      deep: true
    },

    helpersIds(val) {
      // Кидаем сюда массив IDшников форм, которые приходят из селектора хелперов
      if (val.length) { 
        // если массив не пустой оставляем в опциях только те что прищли
        this.selectorOptions = this.allSelectorOptions.filter(item => val.includes(item.id))
        // this.selected = val
      } else {
        // иначе показываем ВСЕ опции
        this.selectorOptions = window.structuredClone(this.allSelectorOptions)
      }
    },
  },

  methods: {
    ...mapActions({
      loadGraph: 'graphql/loadGraph',
    }),

    toggleAll() {
      this.$nextTick(() => {
        if (this.allItems) {
          this.selected = []
        } else {
          this.selected = this.selectorOptions.map((p) => p.id)
        }
      })
    },

    async refresh() {
      this.selected = []
      this.search = ''
      await this.sendRequest()
    },

    async sendRequest() {
      this.loading = true

      const graphQuery = `queues${this.filters} {
        id
        name
        dname
      }`

      await this.loadGraph(graphQuery).then(resp => {
        this.selectorOptions = resp.queues.map(q => ({
          id: q.id,
          name: q.dname ? `${q.name} ${q.dname}` : q.name
        }))

        if (!this.search) {
          this.allSelectorOptions = resp.queues
        }

        this.disabled = false
      })
      this.loading = false
    },
  },

  async mounted() {
    if (this.dontLinkToProject === true) await this.sendRequest()

    this.debounceRequest = this.utils.debounce(this.sendRequest, 700)
  },
}
</script>

<style scoped lang="scss">
.sliced-name {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>